import React from 'react';

function Expertise() {
  return (
    <section id="expertise-section" className="section-padding bg-light">
      <div className="container">
        <h2>Expertise Stack: Dual-Cloud Autonomous Agent Infrastructure</h2>
        <div className="expertise-columns">
          
          {/* GCP Column */}
          <div className="expertise-column gcp-column">
            <h3>Google Cloud Agent Ecosystem</h3>
            <p>
              We design state-of-the-art cognitive layers using Google Cloud's AI suite. Leveraging <strong>Agent Platform</strong> and foundational <strong>Gemini models</strong>, we architect agents with advanced semantic understanding, multimodal reasoning, and rapid MLOps pipelines.
            </p>
            <div className="expertise-benefits">
              <h4>Strategic Capabilities:</h4>
              <ul>
                <li>Multimodal Gemini Prompt Architectures</li>
                <li>LangChain & LlamaIndex Reasoning Chains</li>
                <li>GKE-based scalable Agent Orchestration</li>
                <li>Real-time Vector Search in BigQuery</li>
                <li>Low-latency serverless model inferences</li>
              </ul>
            </div>
            <div className="tech-logos">
              {/* Vertex AI icon replacement */}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ height: '24px', color: 'var(--primary)' }}>
                <path d="m12 3-8 4v10l8 4 8-4V7l-8-4z" />
                <path d="m12 3v18" />
                <path d="m12 12 8-4" />
                <path d="m12 12-8-4" />
              </svg>
              {/* BigQuery icon replacement */}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ height: '24px', color: 'var(--primary)' }}>
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </svg>
              {/* Orchestrator GKE icon replacement */}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ height: '24px', color: 'var(--primary)' }}>
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
          </div>

          {/* Synergy Column */}
          <div className="expertise-column synergy-column">
            <h3>The Power of Integration</h3>
            <div className="synergy-graphic">
              {/* Custom High-Tech SVG Synergy Diagram */}
              <svg viewBox="0 0 200 200" style={{ width: '160px', height: '160px' }}>
                <defs>
                  <linearGradient id="cyanGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="var(--primary)" />
                    <stop offset="100%" stopColor="var(--secondary)" />
                  </linearGradient>
                </defs>
                <circle cx="70" cy="100" r="45" fill="none" stroke="url(#cyanGrad)" strokeWidth="2.5" strokeDasharray="5,5" />
                <circle cx="130" cy="100" r="45" fill="none" stroke="url(#cyanGrad)" strokeWidth="2.5" />
                <polygon points="100,75 115,105 85,105" fill="var(--primary)" />
                <text x="70" y="105" fill="#fff" fontSize="10" fontWeight="bold" textAnchor="middle">GCP</text>
                <text x="130" y="105" fill="#fff" fontSize="10" fontWeight="bold" textAnchor="middle">OCI</text>
                <path d="M100 130 C100 160, 100 160, 100 145" stroke="var(--primary)" strokeWidth="2" />
              </svg>
            </div>
            <p>
              Dual-cloud architectures leverage the best of both platforms. By running GCP's cutting-edge reasoning engines directly alongside OCI's enterprise security layers and autonomous database partitions, we deliver multi-agent workflows that are incredibly smart, responsive, and completely secure.
            </p>
          </div>

          {/* OCI Column */}
          <div className="expertise-column oracle-column">
            <h3>Oracle Cloud Agent Anchor</h3>
            <p>
              We secure agent memory, tool use, and operational logs using OCI's robust enterprise architecture. By integrating <strong>OCI Generative AI services</strong> (Llama & Cohere) with <strong>Oracle Autonomous Database</strong>, we create a secure, self-tuning transactional foundation.
            </p>
            <div className="expertise-benefits">
              <h4>Strategic Capabilities:</h4>
              <ul>
                <li>OCI Generative AI Llama 3 Integration</li>
                <li>AI Vector Search in Autonomous Database</li>
                <li>Secure Database Function-Calling tools</li>
                <li>Strict corporate role-based OCI IAM gates</li>
                <li>OCI Search with OpenSearch log tracking</li>
              </ul>
            </div>
            <div className="tech-logos">
              {/* OCI icon replacement */}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ height: '24px', color: 'var(--secondary)' }}>
                <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              {/* Autonomous Database icon replacement */}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ height: '24px', color: 'var(--secondary)' }}>
                <ellipse cx="12" cy="5" rx="9" ry="3" />
                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
                <path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3" />
              </svg>
            </div>
          </div>

        </div>

        <div className="complementary-tech">
          <h3>Open-Source & Multi-Agent Frameworks</h3>
          <p>
            Our deployment stacks utilize leading industry-standard agent libraries and foundational coding environments to ensure compatibility, speed, and clean codebases.
          </p>
          <div className="tech-logos" style={{ justifyContent: 'center', gap: '30px' }}>
            {/* Python SVG */}
            <svg viewBox="0 0 24 24" style={{ height: '28px' }} fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
            </svg>
            {/* Java SVG */}
            <svg viewBox="0 0 24 24" style={{ height: '28px' }} fill="currentColor">
              <path d="M2 10s2.5-1.5 5-1.5 5 1.5 5 1.5-2.5 1.5-5 1.5-5-1.5-5-1.5zm0 4s2.5-1.5 5-1.5 5 1.5 5 1.5-2.5 1.5-5 1.5-5-1.5-5-1.5z" />
            </svg>
            {/* TensorFlow/PyTorch replacement SVG */}
            <svg viewBox="0 0 24 24" style={{ height: '28px' }} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 2 7 12 12 22 7 12 2" />
              <polyline points="2 17 12 22 22 17" />
              <polyline points="2 12 12 17 22 12" />
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Expertise;
